// ─── New-account SMS warm-up banner ──────────────────────────────────────────
// Renders for the whole 21-day ramp, not only when the cap is hit. Before this
// existed the ramp was completely invisible: a new account uploading 5,000 leads
// watched 1,000 send and the rest go quiet with nothing in the product saying
// the limit existed, that the leads were safe, or when it lifts.
//
// Three things this copy must always carry:
//   1. WHY  — carrier trust, or the limit reads as arbitrary throttling.
//   2. Leads are QUEUED, not lost — that is the actual panic.
//   3. The DATE it ends — turns an open-ended limit into a countdown.
//
// Informational (blue) while there is budget left; amber once the day's budget
// is spent. Never red: nothing has gone wrong.
//
// Reads through the get-warmup-status edge function — tenant_daily_sends is
// service-role only and has no browser-facing RLS path.

import React, { useState, useEffect, useCallback } from 'react';
import { Waves, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import supabase from '../lib/supabaseClient';

// "Aug 12" / "Aug 12 at 8:00 AM" — parsed as local so a bare YYYY-MM-DD does not
// slip a day backwards the way new Date('2026-08-12') (UTC midnight) would.
function formatDay(ymd) {
  if (!ymd) return null;
  const [y, m, d] = String(ymd).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Render an instant in the TENANT'S zone, not the viewer's browser zone — an
// owner checking from a hotel in another state must still see their own hours.
function formatResume(iso, timeZone) {
  if (!iso) return null;
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return null;
  const opts = { hour: 'numeric', minute: '2-digit', weekday: 'short', month: 'short', day: 'numeric' };
  if (timeZone) opts.timeZone = timeZone;
  try {
    return dt.toLocaleString('en-US', opts);
  } catch {
    return dt.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', weekday: 'short', month: 'short', day: 'numeric' });
  }
}

export default function WarmupRampBanner() {
  const [status, setStatus] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/get-warmup-status`,
        { headers: { Authorization: `Bearer ${session.access_token}` } },
      );
      if (!res.ok) return;

      const json = await res.json();
      setStatus(json?.in_warmup ? json : null);
    } catch {
      // Informational only — a failed read renders nothing rather than an error.
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!status) return null;

  const { tenant_day, total_days, daily_cap, used, at_cap, ends_on, resumes_at, timezone, window_capped } = status;

  // Days 15-21 carry a weekly ceiling on top of the daily one. Once the week is
  // spent the effective daily cap is 0, and the daily framing ("you've sent all
  // 0 of today's messages") is nonsense — say it's the weekly total instead.
  const weeklyExhausted = window_capped && daily_cap === 0;

  const remaining = Math.max(daily_cap - used, 0);
  const pct = daily_cap > 0 ? Math.min(Math.round((used / daily_cap) * 100), 100) : 0;
  const endsLabel = formatDay(ends_on);
  const resumeLabel = formatResume(resumes_at, timezone);

  const tone = at_cap
    ? { box: 'bg-amber-50 border-amber-200', icon: 'text-amber-600', head: 'text-amber-900', body: 'text-amber-800', bar: 'bg-amber-500', track: 'bg-amber-100' }
    : { box: 'bg-blue-50 border-blue-200', icon: 'text-blue-600', head: 'text-blue-900', body: 'text-blue-800', bar: 'bg-blue-500', track: 'bg-blue-100' };

  const Icon = at_cap ? Clock : Waves;

  return (
    <div className={`${tone.box} border rounded-lg p-4`} role="status">
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 ${tone.icon} flex-shrink-0 mt-0.5`} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <h4 className={`font-medium ${tone.head}`}>
              {weeklyExhausted
                ? "This week's warm-up limit reached"
                : at_cap
                  ? "Today's warm-up limit reached"
                  : `Account warm-up: day ${tenant_day} of ${total_days}`}
            </h4>
            <button
              type="button"
              onClick={() => setCollapsed(v => !v)}
              className={`${tone.body} hover:opacity-70 flex-shrink-0`}
              aria-label={collapsed ? 'Show warm-up details' : 'Hide warm-up details'}
            >
              {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </button>
          </div>

          {collapsed ? (
            <p className={`text-sm ${tone.body} mt-1`}>
              {weeklyExhausted
                ? "This week's allowance is used"
                : `${used.toLocaleString()} / ${daily_cap.toLocaleString()} sent today`}
              {endsLabel ? ` · full speed ${endsLabel}` : ''}
            </p>
          ) : (
            <>
              <p className={`text-sm ${tone.body} mt-1`}>
                New accounts ramp up gradually so mobile carriers learn to trust your number and account.
                {' '}
                {weeklyExhausted ? (
                  <>
                    You&apos;ve used this week&apos;s full warm-up allowance. Your remaining
                    leads stay queued and go out automatically
                    {resumeLabel ? <> when your sending hours open next, <strong>{resumeLabel}</strong></> : <> when your sending hours open next</>}
                    . Nothing is lost.
                  </>
                ) : at_cap ? (
                  <>
                    You&apos;ve sent all <strong>{daily_cap.toLocaleString()}</strong> of today&apos;s
                    messages. Your remaining leads stay queued and go out automatically
                    {resumeLabel ? <> when your sending hours open next, <strong>{resumeLabel}</strong></> : <> when your sending hours open next</>}
                    . Nothing is lost.
                  </>
                ) : (
                  <>
                    You can send up to <strong>{daily_cap.toLocaleString()}</strong> messages
                    today, with <strong>{remaining.toLocaleString()}</strong> still available.
                    Anything past that queues and goes out when your sending hours open next.
                  </>
                )}
                {endsLabel && <> Limits lift completely on <strong>{endsLabel}</strong>.</>}
              </p>

              {/* A 0-of-0 bar communicates nothing; suppress it when the weekly
                  ceiling, not the daily one, is what is binding. */}
              {!weeklyExhausted && (
                <>
                  <div className={`mt-3 h-1.5 w-full rounded-full ${tone.track} overflow-hidden`}>
                    <div
                      className={`h-full ${tone.bar} rounded-full transition-all duration-500`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <p className={`text-xs ${tone.body} mt-1.5 opacity-80`}>
                    {used.toLocaleString()} of {daily_cap.toLocaleString()} sent today
                  </p>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
